import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { catStyle } from "@/lib/constants";
import { downloadFile, fmtDeadline, fmtDuration } from "@/lib/time";
import { useRoleNames, useStatuses } from "@/hooks/useLookups";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CardTask from "@/components/jobs/CardTask";
import CardEditor from "@/components/jobs/CardEditor";
import DeleteCardButton from "@/components/jobs/DeleteCardButton";
import ProcessesBox from "@/components/jobs/ProcessesBox";
import StatusBox from "@/components/jobs/StatusBox";

function Facts({ c }) {
  const total = c.tasks.reduce((a, t) => a + t.duration_minutes, 0);
  const done = c.tasks.filter((t) => t.status === "done").length;
  const rows = [c.start_date && ["Start date", fmtDeadline(c.start_date)], ["Internal deadline", fmtDeadline(c.deadline)], c.statutory_deadline && ["Deadline (statutory)", fmtDeadline(c.statutory_deadline)],
    ["Tasks done", `${done} of ${c.tasks.length}`], ["Estimated time", fmtDuration(total)]].filter(Boolean);
  return (
    <dl className="rounded-xl border border-[#103080]/10 bg-white p-3 space-y-1.5 text-sm" data-testid="card-facts">
      {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><dt className="text-[#0F172A]/55">{k}</dt><dd className="font-semibold text-[#103080]">{v}</dd></div>)}
    </dl>
  );
}

export default function JobCardDialog({ card, user, people, directory, onClose, onReload, onSchedule, onPatch, onSubtasks }) {
  const [editing, setEditing] = useState(false);
  const statuses = useStatuses();
  const roleNames = useRoleNames();
  useEffect(() => {
    setEditing(false);
  }, [card?.id]);
  if (!card) return null;
  const s = catStyle(card.category);
  const isAdmin = user.role === "admin";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 w-[96vw] max-w-[1400px] h-[92vh] overflow-y-auto p-6 sm:p-8" data-testid="job-card-dialog">
        <DialogHeader className="text-left">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: s.bg, color: s.text }}>{card.category}</span>
            <span className="text-xs text-[#0F172A]/50">{card.period_label}</span>
          </div>
          <DialogTitle className="font-serif-brand text-3xl text-[#103080] mt-1" data-testid="job-card-dialog-title">{card.client_name}</DialogTitle>
          <DialogDescription className="text-base">{card.title}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6 mt-2">
          <div className="min-w-0 space-y-3 order-2 lg:order-1">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-brand font-semibold text-xl text-[#103080]">{editing ? "Edit tasks & subtasks" : "Tasks"}</h3>
              {!editing && (
                <div className="flex flex-wrap gap-2">
                <button onClick={() => downloadFile(api, `/cards/${card.id}/export`, "job-card.xlsx").catch((e) => toast.error(formatApiError(e)))} data-testid="job-card-export-button"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#103080]/20 bg-white px-4 h-9 text-sm font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200"><FileSpreadsheet size={13} /> Export to spreadsheet</button>
                {isAdmin && <button onClick={() => setEditing(true)} data-testid="job-card-edit-button"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#103080]/20 bg-white px-4 h-9 text-sm font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200"><Pencil size={13} /> Edit job card</button>}
                {isAdmin && <DeleteCardButton card={card} onDeleted={() => { onClose(); onReload(); }} />}
                </div>
              )}
            </div>
            {editing ? (
              <CardEditor card={card} isAdmin={isAdmin} directory={directory} roleNames={roleNames} statuses={statuses} defaultAssignee={user.id}
                onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onReload(); }} />
            ) : (
              <ul className="space-y-3" data-testid="job-card-task-list">
                {card.tasks.map((t) => (
                  <CardTask key={t.id} t={t} who={people[t.user_id]} canEdit={isAdmin || t.user_id === user.id} onSchedule={onSchedule} onPatch={onPatch} onSubtasks={onSubtasks} />
                ))}
              </ul>
            )}
          </div>
          <aside className="space-y-4 order-1 lg:order-2">
            <StatusBox card={card} statuses={statuses} onChanged={onReload} readOnly={!isAdmin} />
            <Facts c={card} />
            <ProcessesBox key={card.id} c={card} readOnly={!isAdmin} />
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
